import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import Loader from "../Loader/Loader";
import Pagination from "../Pagination/Pagination";
import { getAuthHeaders } from "../../utils/auth";

const PAGE_SIZE = 10;
const FETCH_ALL_PAGE_SIZE = 100;

const ASSIGNMENT_FILTERS = [
  { value: "all", label: "All" },
  { value: "assigned", label: "Assigned" },
  { value: "unassigned", label: "Unassigned" },
];

const getEmployeeId = (employee) => employee.id || employee.employee_id;

const ShiftEmployeePicker = ({
  selectedEmployeeIds,
  onSelectionChange,
  shiftId = null,
  disabled = false,
}) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [showSelectedFirst, setShowSelectedFirst] = useState(false);
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const headerSelectRef = useRef(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Only refetch for selection changes when "Selected first" ordering is on
  const selectedIdsForFetch = showSelectedFirst
    ? selectedEmployeeIds.join(",")
    : "";

  useEffect(() => {
    setCurrentPage(1);
    setSelectAllActive(false);
  }, [debouncedSearch, showSelectedFirst, assignmentFilter]);

  useEffect(() => {
    if (selectAllActive && selectedEmployeeIds.length < totalCount) {
      setSelectAllActive(false);
    }
  }, [selectedEmployeeIds, selectAllActive, totalCount]);

  useEffect(() => {
    let cancelled = false;

    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const headers = await getAuthHeaders();
        const skip = (currentPage - 1) * PAGE_SIZE;
        const params = {
          skip,
          limit: PAGE_SIZE,
          sort_by: "name",
          sort_order: "asc",
          status: "Active",
          short_response: true,
          ...(assignmentFilter !== "all"
            ? { shift_assignment: assignmentFilter }
            : {}),
          ...(assignmentFilter === "assigned" && shiftId
            ? { shift_id: shiftId }
            : {}),
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(showSelectedFirst && selectedIdsForFetch
            ? {
                selected_first: true,
                selected_ids: selectedIdsForFetch,
              }
            : {}),
        };
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/employees`,
          { headers, params }
        );
        if (cancelled) return;
        const list = response.data.employees || [];
        const total = response.data.total || 0;
        setEmployees(list);
        setTotalCount(total);
        setTotalPages(Math.max(1, Math.ceil(total / PAGE_SIZE)));
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching employees for shift picker:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchEmployees();
    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    debouncedSearch,
    showSelectedFirst,
    selectedIdsForFetch,
    assignmentFilter,
    shiftId,
  ]);

  const selectedSet = new Set(selectedEmployeeIds);
  const pageIds = employees.map(getEmployeeId).filter(Boolean);
  const selectedOnPageCount = pageIds.filter((id) => selectedSet.has(id)).length;
  const allOnPageSelected =
    pageIds.length > 0 && selectedOnPageCount === pageIds.length;
  const someOnPageSelected =
    selectedOnPageCount > 0 && selectedOnPageCount < pageIds.length;

  useEffect(() => {
    if (headerSelectRef.current) {
      headerSelectRef.current.indeterminate = someOnPageSelected;
    }
  }, [someOnPageSelected]);

  const toggleEmployee = (employeeId) => {
    if (disabled || !employeeId) return;
    const next = new Set(selectedSet);
    if (next.has(employeeId)) {
      next.delete(employeeId);
    } else {
      next.add(employeeId);
    }
    onSelectionChange([...next]);
  };

  const toggleSelectPage = (checked) => {
    if (disabled) return;
    const next = new Set(selectedSet);
    if (checked) {
      pageIds.forEach((id) => next.add(id));
    } else {
      pageIds.forEach((id) => next.delete(id));
      setSelectAllActive(false);
    }
    onSelectionChange([...next]);
  };

  const fetchAllMatchingIds = async () => {
    const headers = await getAuthHeaders();
    const ids = [];
    let skip = 0;
    let total = Infinity;

    while (skip < total) {
      const params = {
        skip,
        limit: FETCH_ALL_PAGE_SIZE,
        sort_by: "name",
        sort_order: "asc",
        status: "Active",
        short_response: true,
        ...(assignmentFilter !== "all"
          ? { shift_assignment: assignmentFilter }
          : {}),
        ...(assignmentFilter === "assigned" && shiftId
          ? { shift_id: shiftId }
          : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      };
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees`,
        { headers, params }
      );
      const list = response.data.employees || [];
      total = response.data.total || 0;
      for (const emp of list) {
        const id = getEmployeeId(emp);
        if (id) ids.push(id);
      }
      if (list.length === 0) break;
      skip += FETCH_ALL_PAGE_SIZE;
    }

    return ids;
  };

  const toggleSelectAll = async (checked) => {
    if (disabled || selectAllLoading) return;

    if (!checked) {
      onSelectionChange([]);
      setSelectAllActive(false);
      return;
    }

    try {
      setSelectAllLoading(true);
      const ids = await fetchAllMatchingIds();
      onSelectionChange(ids);
      setSelectAllActive(true);
      setTotalCount(ids.length);
    } catch (error) {
      console.error("Error selecting all employees for shift picker:", error);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const controlsDisabled = disabled || selectAllLoading;

  return (
    <div className="border-t border-gray-200 pt-4 mt-2">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h4 className="text-sm font-semibold text-gray-800">Assign employees</h4>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={assignmentFilter}
            onChange={(e) => setAssignmentFilter(e.target.value)}
            disabled={controlsDisabled}
            aria-label="Filter by shift assignment"
            className="border rounded px-2 py-1 text-xs text-gray-700 bg-white disabled:bg-gray-100"
          >
            {ASSIGNMENT_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={selectAllActive && totalCount > 0}
              onChange={(e) => toggleSelectAll(e.target.checked)}
              disabled={controlsDisabled || totalCount === 0}
              className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936]"
            />
            {selectAllLoading ? "Selecting…" : "Select all"}
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showSelectedFirst}
              onChange={(e) => setShowSelectedFirst(e.target.checked)}
              disabled={controlsDisabled || selectedEmployeeIds.length === 0}
              className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936]"
            />
            Selected first
          </label>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {selectedEmployeeIds.length} selected
          </span>
        </div>
      </div>

      <input
        type="search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search by name, employee number, or email"
        disabled={controlsDisabled}
        className="w-full border rounded px-3 py-2 text-sm mb-3 disabled:bg-gray-100"
      />

      <div className="relative border rounded overflow-hidden min-h-[12rem]">
        {(loading || selectAllLoading) && <Loader />}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-600 uppercase">
              <tr>
                <th className="px-3 py-2 w-10" aria-label="Select">
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={(e) => toggleSelectPage(e.target.checked)}
                    disabled={controlsDisabled || pageIds.length === 0}
                    className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936]"
                    title="Select all on this page"
                    aria-label="Select all on this page"
                  />
                </th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Employee No.</th>
                <th className="px-3 py-2">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {!loading && employees.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                    No employees found.
                  </td>
                </tr>
              )}
              {employees.map((employee) => {
                const employeeId = getEmployeeId(employee);
                const isSelected = selectedSet.has(employeeId);
                const onOtherShift =
                  employee.shift_id &&
                  !isSelected &&
                  employee.shift_name &&
                  employee.shift_name !== "—";

                return (
                  <tr
                    key={employeeId || employee.employee_no}
                    className={isSelected ? "bg-[#f8f5f2]" : "hover:bg-gray-50"}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleEmployee(employeeId)}
                        disabled={controlsDisabled || !employeeId}
                        className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936]"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900 break-words">
                        {employee.name || "—"}
                      </div>
                      {onOtherShift && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          Currently: {employee.shift_name}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {employee.employee_no || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-600 break-all">
                      {employee.email || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-2">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default ShiftEmployeePicker;
